/**
 * "Mark done" dispatched through the task type (spec §9.9 `onDone`): recurring tasks reset their clock
 * and stay open; everything else closes. One entry point so bins, lists and the panel agree.
 */
import { startOfDay } from "./notYet";
import type { LoroDoc } from "loro-crdt";
import { getTaskType } from "../registry/taskTypes";
import { addToGroup, completeRecurring, createTask, markDone, markUndone, snapshotOf } from "./ops";
import { nextOccurrence, parseRrule } from "./rrule";
import { groupsOf } from "./structure";
import { readTask } from "./tasks";
import { C, type PropVal } from "./schema";
import { actorProfile } from "./actor";

export function completeTask(
  doc: LoroDoc,
  id: string,
  opts: { now?: Date; by?: string } = {},
): "keepOpen" | "close" {
  const task = readTask(doc, id);
  if (!task) throw new Error("ops.noSuchTask");
  const type = getTaskType(task.typeId);
  if (type && !type.completable) return "keepOpen";
  const verdict = type?.onDone?.(task, { doc, now: Date.now() }) ?? "close";
  // done already (a second click on a row that has not moved yet, or done on another device meanwhile):
  // nothing to write, and above all no second next occurrence (bug hunt M26)
  if (verdict === "close" && task.doneAt) return verdict;
  // in a shared space every completion says who did it (spec §9.29: doneAt is never written without doneBy)
  if (!opts.by && (doc.getMap(C.settings).get("shared") as boolean | undefined) === true)
    opts = { ...opts, by: actorProfile() ?? undefined };
  if (verdict === "keepOpen") completeRecurring(doc, id, opts);
  else {
    markDone(doc, id, { now: opts.now, doneBy: opts.by });
    cloneIfRepeating(doc, id, opts.now ?? new Date());
  }
  return verdict;
}

export function uncompleteTask(doc: LoroDoc, id: string): void {
  markUndone(doc, id);
}

/**
 * Fixed-date recurrence (spec §9.5, D-8 clone-on-done): a done task with `repeat.rrule` spawns its
 * next occurrence with the next `due`, the same props and groups. Not soft; `recurring` is the soft kind.
 */
export function cloneIfRepeating(doc: LoroDoc, id: string, now: Date): string | null {
  const task = readTask(doc, id);
  const rrule = task?.props["repeat.rrule"];
  if (!task || typeof rrule !== "string" || !rrule) return null;
  const anchor = typeof task.props.due === "string" ? new Date(task.props.due) : new Date(task.createdAt);
  // strictly after both now and the current due, so finishing early does not repeat the same date
  const next = nextOccurrence(rrule, anchor, new Date(Math.max(now.getTime(), anchor.getTime())));
  if (!next) return null;
  // the next occurrence is "not yet" until its day starts, or until the rule's time when it has one ("appears at",
  // spec §9.5): it cannot be ticked off by accident now
  const props: Record<string, PropVal> = {
    ...task.props,
    due: next.toISOString(),
    startAt: (parseRrule(rrule)?.time ? next : startOfDay(next)).toISOString(),
  };
  delete props["notNow.count"];
  delete props["notNow.quietUntil"];
  // a linked task's next occurrence is a new link in the same workspaces (ADR 0024): the server names it
  if (props["link.id"]) {
    props["link.from"] = props["link.id"];
    delete props["link.id"];
  }
  // who did this occurrence goes into the next one's history: whose turn it is follows (spec §9.29)
  if (task.doneBy) {
    const hist = Array.isArray(props["rec.history"]) ? (props["rec.history"] as PropVal[]) : [];
    props["rec.history"] = [...hist, { at: now.toISOString(), by: task.doneBy }].slice(-200) as PropVal;
  }
  const groups = groupsOf(snapshotOf(doc), id);
  const clone = createTask(doc, { title: task.title, typeId: task.typeId, notes: task.notes, props, now });
  for (const g of groups) addToGroup(doc, g, clone);
  return clone;
}
